import { hasLiveSubscription, type BillingSeat } from './BillingMemberRow';

/**
 * When credit may be purchased, and for whom.
 *
 * ── PLAN AND CREDIT ARE NOT THE SAME THING ──────────────────────────────────────────────────────
 * A plan is a per-seat subscription bought from the catalogue; credit is spendable AI budget added on
 * top of whatever a plan grants. Conflating them is the trap here: "the org has a plan" is not a fact
 * this product has, because plans are held by SEATS. `plansAtCapacity` already encodes that — it only
 * calls a plan current when EVERY seat holds it live.
 *
 * ── WHY A LIVE SUBSCRIPTION IS THE CONDITION ────────────────────────────────────────────────────
 * A top-up is "added to this seat's budget on top of whatever its plan grants". A seat with no plan
 * has no budget for it to sit on top of, so buying credit for one is spending money on an allowance
 * nothing consumes. `hasLiveSubscription` is the existing predicate for that state — `recurring` and
 * a status that is neither `none` nor `canceled` — and it is reused rather than re-derived, so a
 * cancelled seat is treated the same way here as it is in the seat table.
 *
 * ── ENTERPRISE FALLS OUT OF THE SAME RULE, IT IS NOT SPECIAL-CASED ──────────────────────────────
 * There is deliberately no `isEnterprise` branch in this file. `BillingPlan` is
 * `'free' | 'starter' | 'growth' | 'scale'` and `BILLING_PLANS` offers starter/growth/scale — there is
 * no enterprise tier, and `isEnterprise` appears in no billing payload at all. So an enterprise
 * connection, billed by contract outside the catalogue, holds no catalogue subscription and therefore
 * has no eligible seat: credit disables as a CONSEQUENCE of the rule above.
 *
 * That matters because the alternative — an explicit "enterprise cannot buy credit" branch — would be
 * inventing a business rule the contract does not state. And if an enterprise connection does carry
 * live subscriptions, this preserves today's behaviour instead of overriding it on a guess.
 */

/** Seats that can receive credit: a live catalogue subscription is what gives credit somewhere to go. */
export function creditEligibleSeats(seats: BillingSeat[]): BillingSeat[] {
  return seats.filter(hasLiveSubscription);
}

/**
 * Why credit cannot be purchased, or `undefined` when it can.
 *
 * Returns the reason rather than a boolean so a disabled control can explain itself — the module's
 * standing rule, and the difference between a greyed-out button and an actionable one.
 *
 * Ordered by what the reader can act on. Authority first: someone who may not manage billing cannot
 * fix a plan problem, so telling them about plans sends them down a road that ends in the same wall.
 */
export function creditPurchaseBlockedReason(
  seats: BillingSeat[],
  options: { canManage: boolean; isLoading: boolean },
): string | undefined {
  if (!options.canManage) return 'Only an org owner or admin can buy credit';
  // Never assert "no seat has a plan" from an unresolved response — that reads as a settled fact.
  if (options.isLoading) return undefined;
  if (seats.length === 0) return 'No members to buy credit for';
  if (creditEligibleSeats(seats).length === 0) {
    return 'Credit tops up a plan’s budget, so a member needs an active plan first';
  }
  return undefined;
}

/** Whether credit may be purchased at all. */
export function canPurchaseCredit(
  seats: BillingSeat[],
  options: { canManage: boolean; isLoading: boolean },
): boolean {
  return creditPurchaseBlockedReason(seats, options) === undefined;
}
